import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, Collapse,
  CircularProgress, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';

const cardSx = {
  background: 'rgba(30,41,59,0.6)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  p: 2,
  mb: 1.5,
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
  },
  '& .MuiInputLabel-root': { color: '#94a3b8' },
  '& .MuiSelect-icon': { color: '#94a3b8' },
};

const menuProps = {
  PaperProps: {
    sx: {
      background: '#1e293b', color: 'white',
      '& .MuiMenuItem-root:hover': { background: 'rgba(255,255,255,0.08)' },
    },
  },
};

export default function DepartmentsManager() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [resultDialog, setResultDialog] = useState({ open: false, message: '', severity: 'success' });

  // Department dialog
  const [deptDialog, setDeptDialog] = useState({ open: false, mode: 'add', data: null });
  const [deptForm, setDeptForm] = useState({ name: '', type: 'Non-Faculty' });
  const [deptSaving, setDeptSaving] = useState(false);

  // Sub-department dialog
  const [subDialog, setSubDialog] = useState({ open: false, mode: 'add', deptId: null, data: null });
  const [subForm, setSubForm] = useState({ name: '' });
  const [subSaving, setSubSaving] = useState(false);

  // Delete confirmations
  const [deleteDept, setDeleteDept] = useState(null);
  const [deleteSub, setDeleteSub] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchDepartments(); }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await api.get('/departments');
      setDepartments(res.data);
    } catch {
      notify('Failed to load departments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const notify = (message, severity = 'success') => setResultDialog({ open: true, message, severity });

  // ── Department operations ────────────────────────────────────────────────────

  const openAddDept = () => {
    setDeptForm({ name: '', type: 'Non-Faculty' });
    setDeptDialog({ open: true, mode: 'add', data: null });
  };

  const openEditDept = (dept) => {
    setDeptForm({ name: dept.name, type: dept.type });
    setDeptDialog({ open: true, mode: 'edit', data: dept });
  };

  const saveDept = async () => {
    if (!deptForm.name.trim() || deptForm.name.trim().length < 2) {
      notify('Department name must be at least 2 characters.', 'error');
      return;
    }
    setDeptSaving(true);
    try {
      if (deptDialog.mode === 'add') {
        const res = await api.post('/departments', deptForm);
        setDepartments(prev => [...prev, { ...res.data, sub_departments: [] }]);
        notify('Department added successfully.');
      } else {
        await api.put(`/departments/${deptDialog.data.id}`, deptForm);
        setDepartments(prev => prev.map(d =>
          d.id === deptDialog.data.id ? { ...d, name: deptForm.name, type: deptForm.type } : d
        ));
        notify('Department updated.');
      }
      setDeptDialog({ open: false, mode: 'add', data: null });
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to save department.', 'error');
    } finally {
      setDeptSaving(false);
    }
  };

  const confirmDeleteDept = async () => {
    setDeleting(true);
    try {
      await api.delete(`/departments/${deleteDept.id}`);
      setDepartments(prev => prev.filter(d => d.id !== deleteDept.id));
      notify('Department deleted.');
      setDeleteDept(null);
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to delete department.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Sub-department operations ────────────────────────────────────────────────

  const openAddSub = (deptId) => {
    setSubForm({ name: '' });
    setSubDialog({ open: true, mode: 'add', deptId, data: null });
  };

  const openEditSub = (deptId, sub) => {
    setSubForm({ name: sub.name });
    setSubDialog({ open: true, mode: 'edit', deptId, data: sub });
  };

  const saveSub = async () => {
    if (!subForm.name.trim() || subForm.name.trim().length < 2) {
      notify('Sub-department name must be at least 2 characters.', 'error');
      return;
    }
    setSubSaving(true);
    try {
      if (subDialog.mode === 'add') {
        const res = await api.post(`/departments/${subDialog.deptId}/sub-departments`, subForm);
        setDepartments(prev => prev.map(d =>
          d.id === subDialog.deptId
            ? { ...d, sub_departments: [...(d.sub_departments || []), res.data] }
            : d
        ));
        notify('Sub-department added.');
      } else {
        const res = await api.put(`/departments/${subDialog.deptId}/sub-departments/${subDialog.data.id}`, subForm);
        setDepartments(prev => prev.map(d =>
          d.id === subDialog.deptId
            ? { ...d, sub_departments: d.sub_departments.map(s => s.id === subDialog.data.id ? res.data : s) }
            : d
        ));
        notify('Sub-department updated.');
      }
      setSubDialog({ open: false, mode: 'add', deptId: null, data: null });
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to save sub-department.', 'error');
    } finally {
      setSubSaving(false);
    }
  };

  const confirmDeleteSub = async () => {
    setDeleting(true);
    try {
      await api.delete(`/departments/${deleteSub.deptId}/sub-departments/${deleteSub.sub.id}`);
      setDepartments(prev => prev.map(d =>
        d.id === deleteSub.deptId
          ? { ...d, sub_departments: d.sub_departments.filter(s => s.id !== deleteSub.sub.id) }
          : d
      ));
      notify('Sub-department deleted.');
      setDeleteSub(null);
    } catch (e) {
      notify(e.response?.data?.message || 'Failed to delete sub-department.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleExpand = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const faculties = departments.filter(d => d.type === 'Faculty');
  const nonFaculties = departments.filter(d => d.type !== 'Faculty');

  const filterDepts = (list) =>
    list.filter(d =>
      d.name.toLowerCase().includes(search.toLowerCase()) ||
      d.sub_departments?.some(s => s.name.toLowerCase().includes(search.toLowerCase()))
    );

  const btnSx = (color, hoverColor) => ({
    textTransform: 'none', fontWeight: 600, fontSize: '0.72rem',
    px: 1.2, py: 0.4, minWidth: 0, borderRadius: '6px',
    color, border: `1px solid ${color}33`,
    '&:hover': { bgcolor: `${color}18`, borderColor: color, color: hoverColor || color },
  });

  const renderDeptCard = (dept) => {
    const isExpanded = expanded[dept.id] ?? false;
    const subCount = dept.sub_departments?.length ?? 0;

    return (
      <Box key={dept.id} sx={cardSx}>
        {/* Department row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ color: 'white', fontWeight: 600, flex: 1, fontSize: '0.92rem', minWidth: 120 }}>
            {dept.name}
          </Typography>
          <Chip
            label={dept.type}
            size="small"
            sx={{
              bgcolor: dept.type === 'Faculty' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)',
              color: dept.type === 'Faculty' ? '#3b82f6' : '#10b981',
              fontSize: '0.68rem', fontWeight: 700,
            }}
          />
          <Button size="small" variant="outlined"
            onClick={() => { openAddSub(dept.id); setExpanded(p => ({ ...p, [dept.id]: true })); }}
            sx={btnSx('#f59e0b')}>
            Add Unit
          </Button>
          <Button size="small" variant="outlined" onClick={() => openEditDept(dept)}
            sx={btnSx('#3b82f6')}>
            Edit
          </Button>
          <Button size="small" variant="outlined" onClick={() => setDeleteDept(dept)}
            sx={btnSx('#ef4444')}>
            Delete
          </Button>
          {subCount > 0 && (
            <Button size="small" variant="text" onClick={() => toggleExpand(dept.id)}
              sx={{ textTransform: 'none', fontSize: '0.72rem', color: '#64748b', px: 1, py: 0.4, minWidth: 0,
                '&:hover': { color: '#94a3b8', bgcolor: 'transparent' } }}>
              {isExpanded ? '▲ Collapse' : `▼ ${subCount} unit${subCount !== 1 ? 's' : ''}`}
            </Button>
          )}
        </Box>

        {/* Sub-departments */}
        <Collapse in={isExpanded && subCount > 0}>
          <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid rgba(255,255,255,0.06)' }}>
            {dept.sub_departments?.map(sub => (
              <Box key={sub.id} sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap',
              }}>
                <Typography sx={{ color: '#cbd5e1', fontSize: '0.82rem', flex: 1, minWidth: 100 }}>{sub.name}</Typography>
                <Button size="small" variant="outlined" onClick={() => openEditSub(dept.id, sub)}
                  sx={btnSx('#3b82f6')}>
                  Edit
                </Button>
                <Button size="small" variant="outlined" onClick={() => setDeleteSub({ deptId: dept.id, sub })}
                  sx={btnSx('#ef4444')}>
                  Delete
                </Button>
              </Box>
            ))}
          </Box>
        </Collapse>

      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" className="gradient-text" sx={{ fontWeight: 'bold' }}>
            Departments & Functional Units
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.82rem', mt: 0.3 }}>
            {departments.length} departments · {departments.reduce((a, d) => a + (d.sub_departments?.length ?? 0), 0)} sub-departments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#64748b', fontSize: 18 }} /></InputAdornment> }}
            sx={{ ...fieldSx, width: 200 }}
          />
          <Button variant="contained" onClick={openAddDept}
            sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 'bold', '&:hover': { bgcolor: '#059669' } }}>
            Add Department
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress sx={{ color: '#10b981' }} />
        </Box>
      ) : (
        <>
          {/* Faculties */}
          {filterDepts(faculties).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ color: '#3b82f6', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 1.5, mb: 1.5, textTransform: 'uppercase' }}>
                Faculties ({filterDepts(faculties).length})
              </Typography>
              {filterDepts(faculties).map(renderDeptCard)}
            </Box>
          )}

          {/* Non-Faculties */}
          {filterDepts(nonFaculties).length > 0 && (
            <Box>
              <Typography sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.72rem', letterSpacing: 1.5, mb: 1.5, textTransform: 'uppercase' }}>
                Non-Faculty Departments ({filterDepts(nonFaculties).length})
              </Typography>
              {filterDepts(nonFaculties).map(renderDeptCard)}
            </Box>
          )}

          {filterDepts([...faculties, ...nonFaculties]).length === 0 && (
            <Typography sx={{ color: '#64748b', textAlign: 'center', mt: 6 }}>No departments match your search.</Typography>
          )}
        </>
      )}

      {/* ── Add/Edit Department Dialog ─────────────────────────────── */}
      <Dialog open={deptDialog.open} onClose={() => setDeptDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px' } }}>
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          {deptDialog.mode === 'add' ? 'Add Department' : 'Edit Department'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '12px !important' }}>
          <TextField fullWidth label="Department Name" value={deptForm.name}
            onChange={e => setDeptForm(p => ({ ...p, name: e.target.value }))} sx={fieldSx} />
          <TextField select fullWidth label="Type" value={deptForm.type}
            onChange={e => setDeptForm(p => ({ ...p, type: e.target.value }))} sx={fieldSx}
            SelectProps={{ MenuProps: menuProps }}>
            <MenuItem value="Faculty">Faculty</MenuItem>
            <MenuItem value="Non-Faculty">Non-Faculty</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeptDialog(p => ({ ...p, open: false }))}
            sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveDept} disabled={deptSaving}
            sx={{ bgcolor: '#10b981', textTransform: 'none', fontWeight: 'bold',
              '&:hover': { bgcolor: '#059669' }, '&.Mui-disabled': { bgcolor: '#10b981', color: 'white', opacity: 0.7 } }}>
            {deptSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Sub-Department Dialog ───────────────────────────── */}
      <Dialog open={subDialog.open} onClose={() => setSubDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px' } }}>
        <DialogTitle sx={{ color: 'white', fontWeight: 'bold' }}>
          {subDialog.mode === 'add' ? 'Add Functional Unit' : 'Edit Functional Unit'}
        </DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <TextField fullWidth label="Functional Unit / Sub-Department Name" value={subForm.name}
            onChange={e => setSubForm({ name: e.target.value })} sx={fieldSx} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setSubDialog(p => ({ ...p, open: false }))}
            sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={saveSub} disabled={subSaving}
            sx={{ bgcolor: '#f59e0b', color: '#000', textTransform: 'none', fontWeight: 'bold',
              '&:hover': { bgcolor: '#d97706' }, '&.Mui-disabled': { bgcolor: '#f59e0b', color: '#000', opacity: 0.7 } }}>
            {subSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Department Confirm ─────────────────────────────────── */}
      <Dialog open={!!deleteDept} onClose={() => setDeleteDept(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: '#0f172a', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '16px' } }}>
        <DialogTitle sx={{ color: '#ef4444', fontWeight: 'bold' }}>Delete Department?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
            This will permanently delete <strong style={{ color: 'white' }}>{deleteDept?.name}</strong> and all its
            {deleteDept?.sub_departments?.length ? ` ${deleteDept.sub_departments.length} functional unit(s)` : ' sub-departments'}.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteDept(null)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={confirmDeleteDept} disabled={deleting}
            sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 'bold',
              '&:hover': { bgcolor: '#dc2626' }, '&.Mui-disabled': { bgcolor: '#ef4444', color: 'white', opacity: 0.7 } }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Sub-Department Confirm ─────────────────────────────── */}
      <Dialog open={!!deleteSub} onClose={() => setDeleteSub(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: '#0f172a', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '16px' } }}>
        <DialogTitle sx={{ color: '#ef4444', fontWeight: 'bold' }}>Delete Functional Unit?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem' }}>
            Remove <strong style={{ color: 'white' }}>{deleteSub?.sub?.name}</strong> permanently?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteSub(null)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={confirmDeleteSub} disabled={deleting}
            sx={{ bgcolor: '#ef4444', textTransform: 'none', fontWeight: 'bold',
              '&:hover': { bgcolor: '#dc2626' }, '&.Mui-disabled': { bgcolor: '#ef4444', color: 'white', opacity: 0.7 } }}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth
        PaperProps={{ sx: { background: '#0f172a', border: `1px solid ${resultDialog.severity === 'error' ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)'}`, borderRadius: '16px', textAlign: 'center' } }}>
        <DialogContent sx={{ py: 4, px: 3 }}>
          <Typography sx={{ color: resultDialog.severity === 'error' ? '#ef4444' : '#10b981', fontWeight: 700, fontSize: '1rem', mb: 1 }}>
            {resultDialog.severity === 'error' ? 'Error' : 'Success'}
          </Typography>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.9rem' }}>
            {resultDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" onClick={() => setResultDialog(p => ({ ...p, open: false }))}
            sx={{ bgcolor: resultDialog.severity === 'error' ? '#ef4444' : '#10b981', textTransform: 'none', fontWeight: 'bold', px: 4,
              '&:hover': { bgcolor: resultDialog.severity === 'error' ? '#dc2626' : '#059669' } }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
